const UPSELL_IMPRESSION_MIN_RATIO = 0.3;
const UPSELL_IMPRESSION_MIN_HEIGHT = 120;

const publishUpsellEvent = (name, payload) => {
  if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
    window.Shopify.analytics.publish(name, payload);
  }
};

const upsellCardPayload = (card) => ({
  productId: card.getAttribute('data-product-id'),
  productHandle: card.getAttribute('data-product-handle'),
  productTitle: card.getAttribute('data-product-title'),
  position: Number(card.getAttribute('data-position'))
});

class CartUpsellTracker extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this._impressionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const drawer = entry.target.closest('cart-drawer');
        const visibleEnough =
          entry.intersectionRatio >= UPSELL_IMPRESSION_MIN_RATIO ||
          entry.intersectionRect.height >= UPSELL_IMPRESSION_MIN_HEIGHT;

        if (!entry.isIntersecting || !visibleEnough || !drawer || !drawer.hasAttribute('open')) {
          return;
        }

        this._impressionObserver.unobserve(entry.target);
        entry.target.setAttribute('data-upsell-viewed', '');

        publishUpsellEvent('upsell_widget_viewed', {
          items: Array.from(entry.target.querySelectorAll('[data-upsell-item]'), upsellCardPayload)
        });
      });
    }, { threshold: [0.05, 0.15, UPSELL_IMPRESSION_MIN_RATIO] });

    this._drawer = document.getElementById('cart-drawer');

    if (this._drawer) {
      this._contentObserver = new MutationObserver(() => this._armImpressionObserver());
      this._contentObserver.observe(this._drawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open']
      });
      this._drawer.addEventListener('dialog:after-show', () => this._armImpressionObserver(), { signal });
    }

    this._armImpressionObserver();

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-upsell-item] a');

      if (!link) {
        return;
      }

      publishUpsellEvent('upsell_widget_product_clicked', upsellCardPayload(link.closest('[data-upsell-item]')));
    }, { signal });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      const formAction = form.getAttribute('action') || '';

      if (form.getAttribute('is') !== 'product-form' && formAction.indexOf('/cart/add') === -1) {
        return;
      }

      if (!form.closest('quick-buy-drawer[data-upsell-source]') && !form.closest('[data-upsell-item]')) {
        return;
      }

      if (!form.querySelector('input[name="properties[_upsell_source]"]')) {
        const propertyInput = document.createElement('input');
        propertyInput.type = 'hidden';
        propertyInput.name = 'properties[_upsell_source]';
        propertyInput.value = 'sidecart';
        form.appendChild(propertyInput);
      }
    }, { signal, capture: true });

    document.addEventListener('variant:add', (event) => {
      const addedItems = event.detail?.items ?? [];
      const upsellItem = addedItems.find((item) => item.properties?._upsell_source === 'sidecart');

      if (!upsellItem) {
        return;
      }

      publishUpsellEvent('upsell_widget_added_to_cart', {
        productId: String(upsellItem.product_id ?? ''),
        variantId: String(upsellItem.id ?? '')
      });
    }, { signal });
  }

  disconnectedCallback() {
    this._abortController.abort();
    this._impressionObserver.disconnect();

    if (this._contentObserver) {
      this._contentObserver.disconnect();
    }
  }

  _armImpressionObserver() {
    if (!this._drawer) {
      return;
    }

    this._drawer.querySelectorAll('.cart-drawer__recommendations:not([data-upsell-viewed])').forEach((block) => {
      this._impressionObserver.unobserve(block);

      if (this._drawer.hasAttribute('open')) {
        this._impressionObserver.observe(block);
      }
    });
  }
}

if (!window.customElements.get('cart-upsell-tracker')) {
  window.customElements.define('cart-upsell-tracker', CartUpsellTracker);
}
